import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Autocomplete,
  autocompleteClasses,
  Box,
  ListItem,
  Typography,
  Popover,
  Paper,
  popoverClasses,
  Tooltip,
} from "@analyticsMD/quil";
import {
  Button,
  InputLabel,
  OutlinedInput,
  FormControl,
  Checkbox,
} from "@mui/material";

import Milestone from "../../../types/Milestone";
import { styles, sxStyles } from "./styles";

interface CustomPaperComponentProps {
  onCancel: () => void;
  onDone: () => void;
}

const CustomPaperComponent: React.FC<CustomPaperComponentProps> = ({
  children,
  onCancel,
  onDone,
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        [`.${autocompleteClasses.noOptions}`]: {
          display: "flex",
          justifyContent: "center",
        },
      }}
    >
      {children}
      <Box sx={sxStyles.buttonsWrapper}>
        <Button size="small" sx={sxStyles.button} onMouseDown={onCancel}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          sx={sxStyles.button}
          onMouseDown={onDone}
        >
          Done
        </Button>
      </Box>
    </Paper>
  );
};

interface MilestonesModalProps {
  open: boolean;
  anchorElement: HTMLElement;
  options: Milestone[];
  onCancel?: () => void;
  onSave?: (milestones?: Milestone[]) => void;
  onClose?: () => void;
}

export interface MilestonesModalHandle {
  focusInput: () => void;
}

const MilestonesModal = forwardRef<MilestonesModalHandle, MilestonesModalProps>(
  ({ open, anchorElement, options, onCancel, onSave, onClose }, ref) => {
    const [tagsOverflow, setTagsOverflow] = useState<boolean>(false);
    // newMilestones keeps up to date the state of autocomplete component
    const [newMilestones, setNewMilestones] = useState([]);
    const boxRef = useRef();
    const inputRef = useRef<HTMLInputElement>(null);
    // newMilestonesRef is used to keep track of newMilestones without causing re-renders
    const newMilestonesRef = useRef([]);

    useImperativeHandle(ref, () => ({
      focusInput: () => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      },
    }));
    // CustomPaperComponent is being memoized to optimize its rendering. Specifically,
    // we want to avoid unnecessary re-renders when the newMilestones value changes,
    // since doing so would reset the scroll position on every item selection.
    const WrappedCustomPaperComponent = useCallback(
      (props) => (
        <CustomPaperComponent
          {...props}
          onCancel={() => {
            setNewMilestones([]);
            onCancel();
          }}
          onDone={() => {
            if (newMilestonesRef.current.length > 0) {
              onSave(newMilestonesRef.current);
              setNewMilestones([]);
            }
            onClose();
          }}
        />
      ),
      [onSave, onCancel, onClose]
    );

    const checkOverflow = (elem: HTMLElement) => {
      //Check overflow by checking if input space is reached its minimum width
      const parentElement = elem.parentElement;
      const inputElement = parentElement.children[1];
      const nodeStyle = window.getComputedStyle(inputElement);
      const paddingLeft = Number(
        nodeStyle.getPropertyValue("padding-left").replace("px", "")
      );
      const paddingRight = Number(
        nodeStyle.getPropertyValue("padding-right").replace("px", "")
      );
      const minWidth = Number(
        nodeStyle.getPropertyValue("min-width").replace("px", "")
      );

      const totalMinWidth = paddingLeft + paddingRight + minWidth;
      const currentWidth = inputElement.clientWidth;
      return currentWidth <= totalMinWidth;
    };

    useEffect(() => {
      if (boxRef.current) {
        setTagsOverflow(checkOverflow(boxRef.current));
      }
    }, [newMilestones]);

    useEffect(() => {
      if (open && inputRef.current) {
        // Focus the input when the modal opens to trigger the autocomplete dropdown
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    }, [open]);
    return (
      <Popover
        elevation={0}
        open={open}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 1,
          horizontal: "left",
        }}
        PaperProps={{
          variant: "outlined",
          style: {
            display: "flex",
            flexDirection: "column",
            overflowY: "hidden",
            width: `${anchorElement?.getBoundingClientRect().width ?? 0}px`,
          },
        }}
        sx={{
          [`.${popoverClasses.paper}`]: {
            borderRadius: "0.25rem",
            border: 0,
          },
        }}
      >
        <Autocomplete
          openOnFocus
          autoHighlight
          multiple
          disableCloseOnSelect
          options={options}
          value={newMilestones}
          componentsProps={{
            popper: {
              placement: "bottom",
              keepMounted: true,
              anchorEl: anchorElement,
              sx: {
                marginTop: "1rem !important",
              },
            },
          }}
          PaperComponent={WrappedCustomPaperComponent}
          ListboxProps={{
            style: sxStyles.listbox,
            className: `${styles.scrollbar}`,
          }}
          sx={{
            [`&.${autocompleteClasses.root}`]: {
              height: "auto",
              minHeight: "2.2rem",
              margin: "0.5rem 0",
            },
            [`& .${autocompleteClasses.inputRoot}`]: {
              gap: "0.25rem",
              padding: "0.4rem 0.75rem",
              flexWrap: "nowrap",
              alignItems: "center",
            },
          }}
          onChange={(event: any, newValue: Milestone[] | null) => {
            newMilestonesRef.current = newValue;
            setNewMilestones(newValue);
          }}
          onClose={() => {
            newMilestonesRef.current = [];
            setNewMilestones([]);
            onClose();
          }}
          getOptionLabel={(option: Milestone) => option.display_name}
          renderOption={(props, option: Milestone, { selected }) => (
            <ListItem
              {...props}
              key={`milestone-option-${option.id}`}
              sx={sxStyles.listItem}
            >
              <Checkbox
                checked={selected}
                size="small"
                sx={sxStyles.checkbox}
              />
              {option.display_name}
            </ListItem>
          )}
          renderInput={(params) => {
            const isEmpty = newMilestones.length === 0;
            return (
              <FormControl
                variant="outlined"
                sx={{
                  width: "100%",
                  [`& .${autocompleteClasses.inputRoot}`]: {
                    flexWrap: "nowrap",
                  },
                  [`& .${autocompleteClasses.input}`]: {
                    flex: isEmpty ? "1 1 auto" : "0 0 0",
                    minWidth: isEmpty ? "50px" : "0 !important",
                    width: isEmpty ? "auto" : "0",
                    padding: isEmpty ? undefined : "0",
                    opacity: isEmpty ? 1 : 0,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                }}
              >
                <InputLabel
                  sx={sxStyles.inputLabel}
                  htmlFor="milestone-input"
                  shrink
                >
                  Milestones
                </InputLabel>
                <OutlinedInput
                  {...params.InputProps}
                  id="milestone-input"
                  style={sxStyles.outlinedInput}
                  label="Milestones"
                  notched
                  inputProps={{
                    ...params.inputProps,
                    ref: (node: HTMLInputElement) => {
                      // Merge refs: call both the Autocomplete's ref and our ref
                      const autocompleteRef = (params.inputProps as any).ref;
                      if (typeof autocompleteRef === "function") {
                        autocompleteRef(node);
                      } else if (autocompleteRef) {
                        (
                          autocompleteRef as React.MutableRefObject<HTMLInputElement | null>
                        ).current = node;
                      }
                      inputRef.current = node;
                    },
                    placeholder: isEmpty ? "Search for milestones" : "",
                    style: sxStyles.outlinedInputProp as React.CSSProperties,
                  }}
                />
              </FormControl>
            );
          }}
          renderTags={(tags) => {
            const allTags = tags.map((t) => t.display_name).join(", ");
            return (
              <Tooltip
                title={<Typography variant="caption">{allTags}</Typography>}
                placement="top"
                disableHoverListener={!tagsOverflow}
                arrow
              >
                <Box ref={boxRef} component="span" sx={sxStyles.tagText}>
                  {allTags}
                </Box>
              </Tooltip>
            );
          }}
        />
      </Popover>
    );
  }
);

MilestonesModal.displayName = "MilestonesModal";

// Memoize the component to prevent unnecessary re-renders caused by parent component
// should be considered as a band-aid rather than a real solution
const memoizedMilestonesModal = React.memo(
  MilestonesModal,
  (prevProps, nextProps) => {
    const prevOptions = prevProps.options
      .map((option) => option.display_name)
      .join(",");
    const nextOptions = nextProps.options
      .map((option) => option.display_name)
      .join(",");
    const optionsChanged = prevOptions !== nextOptions;
    const openChanged = prevProps.open !== nextProps.open;
    // return if props have not changed
    return !optionsChanged && !openChanged;
  }
);

memoizedMilestonesModal.displayName = "MilestonesModal";

export default memoizedMilestonesModal;
